import { Box, Typography } from '@mui/material'
import React from 'react'
import { usePatientFormI18n } from '../utils/patientFormI18n'
import {
  PatientCard,
  PatientChip,
  PatientFieldHint,
  PatientFormCell,
  PatientFormField,
  PatientFormGrid,
  PatientInlineAddButton,
  PatientNativeSelect,
  PatientRowDeleteButton,
  PatientSectionTitle,
  PatientTextInput,
} from './PatientFormPrimitives'

function AttendantCard({
  attendant,
  index,
  editMode,
  showContactFields,
  relationshipOptions,
  validation,
  onUpdate,
  onRemove,
  markTouched,
}) {
  const { copy } = usePatientFormI18n()
  const disabled = !editMode
  const relationValue = relationshipOptions.some((option) => option.value === attendant?.type)
    ? attendant.type
    : relationshipOptions[0]?.value
  const idBase = `patient-attendant-${index}`

  return (
    <PatientCard>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          marginBottom: '10px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <Typography component="div" sx={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>
            {copy.attendantTitle(index)}
          </Typography>
          {attendant?.userId && <PatientChip>{copy.attendantLinkedAccountLabel}</PatientChip>}
        </Box>
        {!disabled && (
          <PatientRowDeleteButton label={copy.removeLabel} onClick={() => onRemove(index)} />
        )}
      </Box>

      <PatientFormGrid sx={{ rowGap: '10px' }}>
        <PatientFormField
          label={copy.nameLabel}
          span={{ xs: 12, md: 7 }}
          htmlFor={`${idBase}-name`}
          error={validation?.showError ? validation.errorMessage : null}
        >
          <PatientTextInput
            id={`${idBase}-name`}
            value={attendant?.name || ''}
            placeholder={copy.attendantNamePlaceholder}
            disabled={disabled}
            invalid={Boolean(validation?.showError)}
            onChange={(event) => {
              if (!disabled) onUpdate(index, 'name', event.target.value)
            }}
            onBlur={() => markTouched?.(`attendant.${index}`)}
          />
        </PatientFormField>

        <PatientFormField
          label={copy.attendantRelationLabel}
          span={{ xs: 12, md: 5 }}
          htmlFor={`${idBase}-relation`}
        >
          <PatientNativeSelect
            id={`${idBase}-relation`}
            value={relationValue}
            disabled={disabled}
            options={relationshipOptions}
            onChange={(event) => {
              if (!disabled) onUpdate(index, 'type', event.target.value)
            }}
          />
        </PatientFormField>

        {showContactFields && (
          <PatientFormField
            label={copy.attendantMobileLabel}
            span={{ xs: 12, md: 6 }}
            htmlFor={`${idBase}-mobile`}
          >
            <PatientTextInput
              id={`${idBase}-mobile`}
              type="tel"
              inputMode="tel"
              autoComplete="off"
              value={attendant?.mobile || ''}
              placeholder={copy.attendantMobilePlaceholder}
              disabled={disabled}
              onChange={(event) => {
                if (!disabled) onUpdate(index, 'mobile', event.target.value)
              }}
            />
          </PatientFormField>
        )}

        {showContactFields && (
          <PatientFormField
            label={copy.attendantEmailLabel}
            span={{ xs: 12, md: 6 }}
            htmlFor={`${idBase}-email`}
          >
            <PatientTextInput
              id={`${idBase}-email`}
              type="email"
              inputMode="email"
              autoComplete="off"
              value={attendant?.email || ''}
              placeholder={copy.attendantEmailPlaceholder}
              disabled={disabled}
              onChange={(event) => {
                if (!disabled) onUpdate(index, 'email', event.target.value)
              }}
            />
          </PatientFormField>
        )}
      </PatientFormGrid>
    </PatientCard>
  )
}

/**
 * Attendants as one card per person (Name | Relation / Mobile | Email). Rows never wrap into a
 * jumble because every input owns a grid cell instead of sharing a flex-wrap line.
 */
export const PatientAttendants = ({
  attendants = [],
  onAdd,
  onUpdate,
  onRemove,
  editMode = true,
  validationHook,
  showContactFields = true,
  title,
}) => {
  const { copy, relationshipOptions } = usePatientFormI18n()
  const rows = Array.isArray(attendants) ? attendants : []

  return (
    <PatientFormCell span={12}>
      <PatientSectionTitle
        action={
          editMode && onAdd ? (
            <PatientInlineAddButton onClick={onAdd}>{copy.addAttendantButton}</PatientInlineAddButton>
          ) : null
        }
      >
        {title || copy.attendantsLabel}
      </PatientSectionTitle>

      {rows.length === 0 ? (
        <PatientFieldHint sx={{ marginTop: '6px' }}>{copy.attendantsEmpty}</PatientFieldHint>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '6px' }}>
          {rows.map((attendant, index) => (
            <AttendantCard
              key={index}
              attendant={attendant}
              index={index}
              editMode={editMode}
              showContactFields={showContactFields}
              relationshipOptions={relationshipOptions}
              validation={validationHook?.getAttendantValidation?.(index)}
              onUpdate={onUpdate}
              onRemove={onRemove}
              markTouched={validationHook?.markTouched}
            />
          ))}
        </Box>
      )}
    </PatientFormCell>
  )
}

export default PatientAttendants
